// Word pools for the Devanāgarī exercise engine. Public reference data — the
// engine draws randomized, non-repeating items from a band and grades on the
// client. Transliteration is IAST. Keep these accurate.
//
// Bands:
//  - simple   : only base consonants carrying the inherent 'a' — no mātrās, no
//               conjuncts. Reads exactly as written, akṣara by akṣara.
//  - cv        : consonant + vowel (mātrā) combinations, still no conjuncts.
//  - conjunct  : contains at least one conjunct (saṃyuktākṣara) and/or special
//               signs (anusvāra ṃ, visarga ḥ, virāma).
//  - mixed     : sampled across all of the above (handled by the engine).
//
// Within the 'devanagari' pool, every `iast` is UNIQUE so that multiple-choice
// prompts have exactly one correct answer.

export type WordBandKey = 'simple' | 'cv' | 'conjunct';

export interface WordItem {
  id: string;
  deva: string;
  iast: string;
  /**
   * For script pools: the English meaning. For grammar pools: the grammatical
   * LABEL (e.g. "instrumental", "3rd person sg.") — this is the answer/options
   * for `pick-gloss` and the prompt for `pick-form`.
   */
  gloss: string;
  /** Optional feedback context shown after grading (e.g. the case's role). */
  note?: string;
  band: WordBandKey;
}

const DEVANAGARI: WordItem[] = [
  // --- simple: base consonants + inherent a (NO mātrā, NO conjunct) ---------
  { id: 'gaja', deva: 'गज', iast: 'gaja', gloss: 'elephant', band: 'simple' },
  { id: 'nabha', deva: 'नभ', iast: 'nabha', gloss: 'sky', band: 'simple' },
  { id: 'kamala', deva: 'कमल', iast: 'kamala', gloss: 'lotus', band: 'simple' },
  { id: 'bhavana', deva: 'भवन', iast: 'bhavana', gloss: 'house, abode', band: 'simple' },
  { id: 'vacana', deva: 'वचन', iast: 'vacana', gloss: 'word, speech', band: 'simple' },
  { id: 'nayana', deva: 'नयन', iast: 'nayana', gloss: 'eye', band: 'simple' },
  { id: 'kanaka', deva: 'कनक', iast: 'kanaka', gloss: 'gold', band: 'simple' },
  { id: 'gagana', deva: 'गगन', iast: 'gagana', gloss: 'sky', band: 'simple' },
  { id: 'pavana', deva: 'पवन', iast: 'pavana', gloss: 'wind, purifier', band: 'simple' },
  { id: 'rajata', deva: 'रजत', iast: 'rajata', gloss: 'silver', band: 'simple' },
  { id: 'nagara', deva: 'नगर', iast: 'nagara', gloss: 'city, town', band: 'simple' },
  { id: 'jala', deva: 'जल', iast: 'jala', gloss: 'water', band: 'simple' },
  { id: 'phala', deva: 'फल', iast: 'phala', gloss: 'fruit, result', band: 'simple' },
  { id: 'bala', deva: 'बल', iast: 'bala', gloss: 'strength', band: 'simple' },
  { id: 'nara', deva: 'नर', iast: 'nara', gloss: 'man', band: 'simple' },
  { id: 'kara', deva: 'कर', iast: 'kara', gloss: 'hand; ray; doer', band: 'simple' },
  { id: 'vana', deva: 'वन', iast: 'vana', gloss: 'forest', band: 'simple' },
  { id: 'nada', deva: 'नद', iast: 'nada', gloss: 'river, roarer', band: 'simple' },
  { id: 'rasa', deva: 'रस', iast: 'rasa', gloss: 'juice, essence, taste', band: 'simple' },
  { id: 'haya', deva: 'हय', iast: 'haya', gloss: 'horse', band: 'simple' },
  { id: 'hala', deva: 'हल', iast: 'hala', gloss: 'plough', band: 'simple' },
  { id: 'sama', deva: 'सम', iast: 'sama', gloss: 'equal, even', band: 'simple' },
  { id: 'shama', deva: 'शम', iast: 'śama', gloss: 'calm, tranquility', band: 'simple' },
  { id: 'bhaya', deva: 'भय', iast: 'bhaya', gloss: 'fear', band: 'simple' },
  { id: 'jaya', deva: 'जय', iast: 'jaya', gloss: 'victory', band: 'simple' },
  { id: 'naya', deva: 'नय', iast: 'naya', gloss: 'guidance, policy', band: 'simple' },
  { id: 'laya', deva: 'लय', iast: 'laya', gloss: 'dissolution, rhythm', band: 'simple' },
  { id: 'vara', deva: 'वर', iast: 'vara', gloss: 'boon, excellent', band: 'simple' },
  { id: 'dasha', deva: 'दश', iast: 'daśa', gloss: 'ten', band: 'simple' },
  { id: 'gana', deva: 'गण', iast: 'gaṇa', gloss: 'group, troop', band: 'simple' },
  { id: 'mada', deva: 'मद', iast: 'mada', gloss: 'intoxication, pride', band: 'simple' },
  { id: 'pada', deva: 'पद', iast: 'pada', gloss: 'foot, step, word', band: 'simple' },
  { id: 'tapa', deva: 'तप', iast: 'tapa', gloss: 'heat, austerity', band: 'simple' },
  { id: 'japa', deva: 'जप', iast: 'japa', gloss: 'muttered prayer', band: 'simple' },
  { id: 'ghana', deva: 'घन', iast: 'ghana', gloss: 'cloud; dense', band: 'simple' },
  { id: 'mata', deva: 'मत', iast: 'mata', gloss: 'opinion, thought', band: 'simple' },
  { id: 'gata', deva: 'गत', iast: 'gata', gloss: 'gone', band: 'simple' },
  { id: 'nata', deva: 'नत', iast: 'nata', gloss: 'bowed, bent', band: 'simple' },
  { id: 'carana', deva: 'चरण', iast: 'caraṇa', gloss: 'foot; conduct', band: 'simple' },
  { id: 'sharana', deva: 'शरण', iast: 'śaraṇa', gloss: 'refuge, shelter', band: 'simple' },
  { id: 'marana', deva: 'मरण', iast: 'maraṇa', gloss: 'death', band: 'simple' },
  { id: 'dahana', deva: 'दहन', iast: 'dahana', gloss: 'burning, fire', band: 'simple' },
  { id: 'vahana', deva: 'वहन', iast: 'vahana', gloss: 'carrying, conveyance', band: 'simple' },
  { id: 'gamana', deva: 'गमन', iast: 'gamana', gloss: 'going, motion', band: 'simple' },
  { id: 'sadana', deva: 'सदन', iast: 'sadana', gloss: 'seat, dwelling', band: 'simple' },
  { id: 'madana', deva: 'मदन', iast: 'madana', gloss: 'passion; Kāma', band: 'simple' },
  { id: 'ramana', deva: 'रमण', iast: 'ramaṇa', gloss: 'delighting; lover', band: 'simple' },
  { id: 'bhajana', deva: 'भजन', iast: 'bhajana', gloss: 'worship, devotional song', band: 'simple' },
  { id: 'vadana', deva: 'वदन', iast: 'vadana', gloss: 'face, mouth', band: 'simple' },
  { id: 'namana', deva: 'नमन', iast: 'namana', gloss: 'bowing, salutation', band: 'simple' },
  // Short members of the length-contrast pairs Lesson 5 drills (nama/nāma,
  // rama/rāma): both are bare-consonant CV-a words, so they live in 'simple'.
  { id: 'nama', deva: 'नम', iast: 'nama', gloss: 'bow!, homage (imperative)', band: 'simple' },
  { id: 'rama', deva: 'रम', iast: 'rama', gloss: 'delight!, be at rest (imperative)', band: 'simple' },

  // --- cv: vowel (mātrā) combinations, no conjuncts -------------------------
  { id: 'rama', deva: 'राम', iast: 'rāma', gloss: 'Rāma', band: 'cv' },
  { id: 'nama', deva: 'नाम', iast: 'nāma', gloss: 'name', band: 'cv' },
  { id: 'kama', deva: 'काम', iast: 'kāma', gloss: 'desire, love', band: 'cv' },
  { id: 'dana', deva: 'दान', iast: 'dāna', gloss: 'gift, charity', band: 'cv' },
  { id: 'mala', deva: 'माला', iast: 'mālā', gloss: 'garland, rosary', band: 'cv' },
  { id: 'sara', deva: 'सार', iast: 'sāra', gloss: 'essence, core', band: 'cv' },
  { id: 'tara', deva: 'तारा', iast: 'tārā', gloss: 'star', band: 'cv' },
  { id: 'dhara', deva: 'धारा', iast: 'dhārā', gloss: 'stream, flow', band: 'cv' },
  { id: 'nadi', deva: 'नदी', iast: 'nadī', gloss: 'river', band: 'cv' },
  { id: 'giri', deva: 'गिरि', iast: 'giri', gloss: 'mountain', band: 'cv' },
  { id: 'ravi', deva: 'रवि', iast: 'ravi', gloss: 'sun', band: 'cv' },
  { id: 'kavi', deva: 'कवि', iast: 'kavi', gloss: 'poet', band: 'cv' },
  { id: 'mani', deva: 'मणि', iast: 'maṇi', gloss: 'jewel', band: 'cv' },
  { id: 'shiva', deva: 'शिव', iast: 'śiva', gloss: 'Śiva; auspicious', band: 'cv' },
  { id: 'dina', deva: 'दिन', iast: 'dina', gloss: 'day', band: 'cv' },
  { id: 'gati', deva: 'गति', iast: 'gati', gloss: 'motion, way, state', band: 'cv' },
  { id: 'mati', deva: 'मति', iast: 'mati', gloss: 'mind, intellect', band: 'cv' },
  { id: 'pati', deva: 'पति', iast: 'pati', gloss: 'lord, husband', band: 'cv' },
  { id: 'rati', deva: 'रति', iast: 'rati', gloss: 'pleasure, love', band: 'cv' },
  { id: 'visha', deva: 'विष', iast: 'viṣa', gloss: 'poison', band: 'cv' },
  { id: 'nari', deva: 'नारी', iast: 'nārī', gloss: 'woman', band: 'cv' },
  { id: 'vani', deva: 'वाणी', iast: 'vāṇī', gloss: 'speech; Sarasvatī', band: 'cv' },
  { id: 'guru', deva: 'गुरु', iast: 'guru', gloss: 'teacher', band: 'cv' },
  { id: 'sukha', deva: 'सुख', iast: 'sukha', gloss: 'happiness, ease', band: 'cv' },
  { id: 'mukha', deva: 'मुख', iast: 'mukha', gloss: 'face, mouth', band: 'cv' },
  { id: 'kula', deva: 'कुल', iast: 'kula', gloss: 'family, clan', band: 'cv' },
  { id: 'muni', deva: 'मुनि', iast: 'muni', gloss: 'sage', band: 'cv' },
  { id: 'pura', deva: 'पुर', iast: 'pura', gloss: 'city, fortress', band: 'cv' },
  { id: 'guna', deva: 'गुण', iast: 'guṇa', gloss: 'quality, virtue, strand', band: 'cv' },
  { id: 'bhanu', deva: 'भानु', iast: 'bhānu', gloss: 'sun, light', band: 'cv' },
  { id: 'madhu', deva: 'मधु', iast: 'madhu', gloss: 'honey, sweet', band: 'cv' },
  { id: 'vayu', deva: 'वायु', iast: 'vāyu', gloss: 'wind, air', band: 'cv' },
  { id: 'pashu', deva: 'पशु', iast: 'paśu', gloss: 'animal, cattle', band: 'cv' },
  { id: 'sura', deva: 'सुर', iast: 'sura', gloss: 'god', band: 'cv' },
  { id: 'asura', deva: 'असुर', iast: 'asura', gloss: 'demon', band: 'cv' },
  { id: 'kumara', deva: 'कुमार', iast: 'kumāra', gloss: 'boy, prince', band: 'cv' },
  { id: 'rupa', deva: 'रूप', iast: 'rūpa', gloss: 'form, beauty', band: 'cv' },
  { id: 'bhumi', deva: 'भूमि', iast: 'bhūmi', gloss: 'earth, ground', band: 'cv' },
  { id: 'dhuma', deva: 'धूम', iast: 'dhūma', gloss: 'smoke', band: 'cv' },
  { id: 'mula', deva: 'मूल', iast: 'mūla', gloss: 'root, base', band: 'cv' },
  // More single-sign words that reinforce the ā / ī length contrast Lesson 5
  // teaches — including dīna, the long partner of dina ("day") drilled there.
  { id: 'dina2', deva: 'दीन', iast: 'dīna', gloss: 'poor, wretched', band: 'cv' },
  { id: 'vira', deva: 'वीर', iast: 'vīra', gloss: 'hero, brave', band: 'cv' },
  { id: 'tira', deva: 'तीर', iast: 'tīra', gloss: 'bank, shore', band: 'cv' },
  { id: 'kala', deva: 'काल', iast: 'kāla', gloss: 'time; death', band: 'cv' },
  { id: 'bhara', deva: 'भार', iast: 'bhāra', gloss: 'burden, load', band: 'cv' },
  { id: 'vata', deva: 'वात', iast: 'vāta', gloss: 'wind, air', band: 'cv' },
  { id: 'taru', deva: 'तरु', iast: 'taru', gloss: 'tree', band: 'cv' },
  { id: 'grha', deva: 'गृह', iast: 'gṛha', gloss: 'house, home', band: 'cv' },
  { id: 'mrga', deva: 'मृग', iast: 'mṛga', gloss: 'deer', band: 'cv' },
  { id: 'trna', deva: 'तृण', iast: 'tṛṇa', gloss: 'grass', band: 'cv' },
  { id: 'ghrta', deva: 'घृत', iast: 'ghṛta', gloss: 'ghee', band: 'cv' },
  { id: 'nrpa', deva: 'नृप', iast: 'nṛpa', gloss: 'king', band: 'cv' },
  { id: 'krta', deva: 'कृत', iast: 'kṛta', gloss: 'done, made', band: 'cv' },
  { id: 'mrta', deva: 'मृत', iast: 'mṛta', gloss: 'dead', band: 'cv' },
  { id: 'hrdaya', deva: 'हृदय', iast: 'hṛdaya', gloss: 'heart', band: 'cv' },
  { id: 'pitr', deva: 'पितृ', iast: 'pitṛ', gloss: 'father; ancestor', band: 'cv' },
  { id: 'matr', deva: 'मातृ', iast: 'mātṛ', gloss: 'mother', band: 'cv' },
  { id: 'deva', deva: 'देव', iast: 'deva', gloss: 'god', band: 'cv' },
  { id: 'devi', deva: 'देवी', iast: 'devī', gloss: 'goddess', band: 'cv' },
  { id: 'veda', deva: 'वेद', iast: 'veda', gloss: 'knowledge; the Vedas', band: 'cv' },
  { id: 'sena', deva: 'सेना', iast: 'senā', gloss: 'army', band: 'cv' },
  { id: 'megha', deva: 'मेघ', iast: 'megha', gloss: 'cloud', band: 'cv' },
  { id: 'kesha', deva: 'केश', iast: 'keśa', gloss: 'hair', band: 'cv' },
  { id: 'hema', deva: 'हेम', iast: 'hema', gloss: 'gold', band: 'cv' },
  { id: 'seva', deva: 'सेवा', iast: 'sevā', gloss: 'service, worship', band: 'cv' },
  { id: 'venu', deva: 'वेणु', iast: 'veṇu', gloss: 'bamboo, flute', band: 'cv' },
  { id: 'geha', deva: 'गेह', iast: 'geha', gloss: 'house, home', band: 'cv' },
  { id: 'vaira', deva: 'वैर', iast: 'vaira', gloss: 'enmity', band: 'cv' },
  { id: 'daiva', deva: 'दैव', iast: 'daiva', gloss: 'fate, divine', band: 'cv' },
  { id: 'shaila', deva: 'शैल', iast: 'śaila', gloss: 'mountain, rocky', band: 'cv' },
  { id: 'gaura', deva: 'गौर', iast: 'gaura', gloss: 'fair, white', band: 'cv' },
  { id: 'mauna', deva: 'मौन', iast: 'mauna', gloss: 'silence', band: 'cv' },
  { id: 'go', deva: 'गो', iast: 'go', gloss: 'cow; the earth; speech', band: 'cv' },
  { id: 'soma', deva: 'सोम', iast: 'soma', gloss: 'Soma (plant/moon/deity)', band: 'cv' },
  { id: 'loka', deva: 'लोक', iast: 'loka', gloss: 'world, realm', band: 'cv' },
  { id: 'moha', deva: 'मोह', iast: 'moha', gloss: 'delusion', band: 'cv' },
  { id: 'shoka', deva: 'शोक', iast: 'śoka', gloss: 'grief', band: 'cv' },
  { id: 'yoga', deva: 'योग', iast: 'yoga', gloss: 'union, discipline, yoga', band: 'cv' },
  { id: 'bhoga', deva: 'भोग', iast: 'bhoga', gloss: 'enjoyment, experience', band: 'cv' },
  { id: 'roga', deva: 'रोग', iast: 'roga', gloss: 'disease', band: 'cv' },
  { id: 'kosha', deva: 'कोश', iast: 'kośa', gloss: 'treasury, sheath, dictionary', band: 'cv' },
  { id: 'homa', deva: 'होम', iast: 'homa', gloss: 'fire-oblation', band: 'cv' },
  { id: 'setu', deva: 'सेतु', iast: 'setu', gloss: 'bridge', band: 'cv' },
  { id: 'ketu', deva: 'केतु', iast: 'ketu', gloss: 'banner; Ketu', band: 'cv' },
  { id: 'gopi', deva: 'गोपी', iast: 'gopī', gloss: 'cowherdess, gopī', band: 'cv' },
  { id: 'rajani', deva: 'रजनी', iast: 'rajanī', gloss: 'night', band: 'cv' },
  { id: 'shashi', deva: 'शशी', iast: 'śaśī', gloss: 'moon', band: 'cv' },
  { id: 'ruci', deva: 'रुचि', iast: 'ruci', gloss: 'taste, liking, splendor', band: 'cv' },
  { id: 'ripu', deva: 'रिपु', iast: 'ripu', gloss: 'enemy', band: 'cv' },
  { id: 'mrdu', deva: 'मृदु', iast: 'mṛdu', gloss: 'soft, gentle', band: 'cv' },
  { id: 'vrka', deva: 'वृक', iast: 'vṛka', gloss: 'wolf', band: 'cv' },
  { id: 'krpa', deva: 'कृपा', iast: 'kṛpā', gloss: 'compassion, grace', band: 'cv' },

  // --- conjunct: conjuncts, repha/rakāra, special ligatures, anusvāra,
  //     visarga, virāma --------------------------------------------------
  { id: 'satya', deva: 'सत्य', iast: 'satya', gloss: 'truth', band: 'conjunct' },
  { id: 'nitya', deva: 'नित्य', iast: 'nitya', gloss: 'eternal, daily', band: 'conjunct' },
  { id: 'vidya', deva: 'विद्या', iast: 'vidyā', gloss: 'knowledge, learning', band: 'conjunct' },
  { id: 'kanya', deva: 'कन्या', iast: 'kanyā', gloss: 'girl, maiden', band: 'conjunct' },
  { id: 'anya', deva: 'अन्य', iast: 'anya', gloss: 'other', band: 'conjunct' },
  { id: 'sva', deva: 'स्व', iast: 'sva', gloss: 'own, self', band: 'conjunct' },
  { id: 'svara', deva: 'स्वर', iast: 'svara', gloss: 'sound, vowel, note', band: 'conjunct' },
  { id: 'dvara', deva: 'द्वार', iast: 'dvāra', gloss: 'door, gate', band: 'conjunct' },
  { id: 'jvara', deva: 'ज्वर', iast: 'jvara', gloss: 'fever', band: 'conjunct' },
  { id: 'sthala', deva: 'स्थल', iast: 'sthala', gloss: 'place, ground', band: 'conjunct' },
  { id: 'snana', deva: 'स्नान', iast: 'snāna', gloss: 'bath', band: 'conjunct' },
  { id: 'smara', deva: 'स्मर', iast: 'smara', gloss: 'remember; love/Kāma', band: 'conjunct' },
  { id: 'ishta', deva: 'इष्ट', iast: 'iṣṭa', gloss: 'desired, beloved', band: 'conjunct' },
  { id: 'kashta', deva: 'कष्ट', iast: 'kaṣṭa', gloss: 'difficulty, suffering', band: 'conjunct' },
  { id: 'dushta', deva: 'दुष्ट', iast: 'duṣṭa', gloss: 'wicked', band: 'conjunct' },
  { id: 'nashta', deva: 'नष्ट', iast: 'naṣṭa', gloss: 'lost, destroyed', band: 'conjunct' },
  { id: 'vrddha', deva: 'वृद्ध', iast: 'vṛddha', gloss: 'old, grown', band: 'conjunct' },
  { id: 'shuddha', deva: 'शुद्ध', iast: 'śuddha', gloss: 'pure, clean', band: 'conjunct' },
  { id: 'siddha', deva: 'सिद्ध', iast: 'siddha', gloss: 'accomplished, perfected', band: 'conjunct' },
  { id: 'buddha', deva: 'बुद्ध', iast: 'buddha', gloss: 'awakened, the Buddha', band: 'conjunct' },
  { id: 'yuddha', deva: 'युद्ध', iast: 'yuddha', gloss: 'war, battle', band: 'conjunct' },
  { id: 'madhya', deva: 'मध्य', iast: 'madhya', gloss: 'middle, center', band: 'conjunct' },
  { id: 'anga', deva: 'अङ्ग', iast: 'aṅga', gloss: 'limb, body, part', band: 'conjunct' },
  { id: 'ganga', deva: 'गङ्गा', iast: 'gaṅgā', gloss: 'the Ganges', band: 'conjunct' },
  { id: 'linga', deva: 'लिङ्ग', iast: 'liṅga', gloss: 'mark, sign, gender', band: 'conjunct' },
  { id: 'shankha', deva: 'शङ्ख', iast: 'śaṅkha', gloss: 'conch shell', band: 'conjunct' },
  { id: 'panca', deva: 'पञ्च', iast: 'pañca', gloss: 'five', band: 'conjunct' },
  { id: 'danda', deva: 'दण्ड', iast: 'daṇḍa', gloss: 'staff, rod, punishment', band: 'conjunct' },
  { id: 'khanda', deva: 'खण्ड', iast: 'khaṇḍa', gloss: 'piece, section', band: 'conjunct' },
  { id: 'gandha', deva: 'गन्ध', iast: 'gandha', gloss: 'smell, fragrance', band: 'conjunct' },
  { id: 'bandha', deva: 'बन्ध', iast: 'bandha', gloss: 'bond, binding', band: 'conjunct' },
  { id: 'sindhu', deva: 'सिन्धु', iast: 'sindhu', gloss: 'river, ocean', band: 'conjunct' },
  { id: 'bhakta', deva: 'भक्त', iast: 'bhakta', gloss: 'devotee, devoted', band: 'conjunct' },
  { id: 'mukta', deva: 'मुक्त', iast: 'mukta', gloss: 'freed, liberated', band: 'conjunct' },
  { id: 'yukta', deva: 'युक्त', iast: 'yukta', gloss: 'joined, suitable', band: 'conjunct' },
  { id: 'vishnu', deva: 'विष्णु', iast: 'viṣṇu', gloss: 'Viṣṇu', band: 'conjunct' },
  { id: 'krishna', deva: 'कृष्ण', iast: 'kṛṣṇa', gloss: 'Kṛṣṇa; black', band: 'conjunct' },
  { id: 'ushna', deva: 'उष्ण', iast: 'uṣṇa', gloss: 'hot, warm', band: 'conjunct' },
  { id: 'padma', deva: 'पद्म', iast: 'padma', gloss: 'lotus', band: 'conjunct' },
  { id: 'tattva', deva: 'तत्त्व', iast: 'tattva', gloss: 'reality, principle, that-ness', band: 'conjunct' },
  { id: 'sattva', deva: 'सत्त्व', iast: 'sattva', gloss: 'being, goodness, essence', band: 'conjunct' },
  { id: 'karma', deva: 'कर्म', iast: 'karma', gloss: 'action, deed, karma', band: 'conjunct' },
  { id: 'dharma', deva: 'धर्म', iast: 'dharma', gloss: 'law, duty, righteousness', band: 'conjunct' },
  { id: 'artha', deva: 'अर्थ', iast: 'artha', gloss: 'meaning, wealth, purpose', band: 'conjunct' },
  { id: 'surya', deva: 'सूर्य', iast: 'sūrya', gloss: 'sun', band: 'conjunct' },
  { id: 'karya', deva: 'कार्य', iast: 'kārya', gloss: 'work, task, effect', band: 'conjunct' },
  { id: 'varna', deva: 'वर्ण', iast: 'varṇa', gloss: 'color, class, letter', band: 'conjunct' },
  { id: 'garbha', deva: 'गर्भ', iast: 'garbha', gloss: 'womb, embryo', band: 'conjunct' },
  { id: 'darshana', deva: 'दर्शन', iast: 'darśana', gloss: 'seeing, vision, philosophy', band: 'conjunct' },
  { id: 'sarva', deva: 'सर्व', iast: 'sarva', gloss: 'all, every', band: 'conjunct' },
  { id: 'purva', deva: 'पूर्व', iast: 'pūrva', gloss: 'prior, former, eastern', band: 'conjunct' },
  { id: 'kirti', deva: 'कीर्ति', iast: 'kīrti', gloss: 'fame, glory', band: 'conjunct' },
  { id: 'murti', deva: 'मूर्ति', iast: 'mūrti', gloss: 'form, image, idol', band: 'conjunct' },
  { id: 'svarga', deva: 'स्वर्ग', iast: 'svarga', gloss: 'heaven', band: 'conjunct' },
  { id: 'arjuna', deva: 'अर्जुन', iast: 'arjuna', gloss: 'Arjuna; bright', band: 'conjunct' },
  { id: 'prabhu', deva: 'प्रभु', iast: 'prabhu', gloss: 'lord, master', band: 'conjunct' },
  { id: 'priya', deva: 'प्रिय', iast: 'priya', gloss: 'dear, beloved', band: 'conjunct' },
  { id: 'prana', deva: 'प्राण', iast: 'prāṇa', gloss: 'breath, life-force', band: 'conjunct' },
  { id: 'grama', deva: 'ग्राम', iast: 'grāma', gloss: 'village', band: 'conjunct' },
  { id: 'krama', deva: 'क्रम', iast: 'krama', gloss: 'step, order, method', band: 'conjunct' },
  { id: 'kriya', deva: 'क्रिया', iast: 'kriyā', gloss: 'action, rite, verb', band: 'conjunct' },
  { id: 'vrata', deva: 'व्रत', iast: 'vrata', gloss: 'vow, observance', band: 'conjunct' },
  { id: 'prema', deva: 'प्रेम', iast: 'prema', gloss: 'love, affection', band: 'conjunct' },
  { id: 'graha', deva: 'ग्रह', iast: 'graha', gloss: 'planet, seizing', band: 'conjunct' },
  { id: 'prasada', deva: 'प्रसाद', iast: 'prasāda', gloss: 'grace, favor, consecrated food', band: 'conjunct' },
  { id: 'prakasha', deva: 'प्रकाश', iast: 'prakāśa', gloss: 'light, manifestation', band: 'conjunct' },
  { id: 'krodha', deva: 'क्रोध', iast: 'krodha', gloss: 'anger, wrath', band: 'conjunct' },
  { id: 'kshetra', deva: 'क्षेत्र', iast: 'kṣetra', gloss: 'field, sacred place', band: 'conjunct' },
  { id: 'akshara', deva: 'अक्षर', iast: 'akṣara', gloss: 'syllable, imperishable', band: 'conjunct' },
  { id: 'kshama', deva: 'क्षमा', iast: 'kṣamā', gloss: 'patience, forgiveness', band: 'conjunct' },
  { id: 'moksha', deva: 'मोक्ष', iast: 'mokṣa', gloss: 'liberation', band: 'conjunct' },
  { id: 'kshatriya', deva: 'क्षत्रिय', iast: 'kṣatriya', gloss: 'warrior class', band: 'conjunct' },
  { id: 'lakshmi', deva: 'लक्ष्मी', iast: 'lakṣmī', gloss: 'Lakṣmī; fortune', band: 'conjunct' },
  { id: 'jnana', deva: 'ज्ञान', iast: 'jñāna', gloss: 'knowledge, wisdom', band: 'conjunct' },
  { id: 'yajna', deva: 'यज्ञ', iast: 'yajña', gloss: 'sacrifice, offering', band: 'conjunct' },
  { id: 'prajna', deva: 'प्रज्ञा', iast: 'prajñā', gloss: 'wisdom, insight', band: 'conjunct' },
  { id: 'vijnana', deva: 'विज्ञान', iast: 'vijñāna', gloss: 'science, discernment', band: 'conjunct' },
  { id: 'mitra', deva: 'मित्र', iast: 'mitra', gloss: 'friend', band: 'conjunct' },
  { id: 'netra', deva: 'नेत्र', iast: 'netra', gloss: 'eye', band: 'conjunct' },
  { id: 'sutra', deva: 'सूत्र', iast: 'sūtra', gloss: 'thread, aphorism, rule', band: 'conjunct' },
  { id: 'mantra', deva: 'मन्त्र', iast: 'mantra', gloss: 'sacred utterance, counsel', band: 'conjunct' },
  { id: 'patra', deva: 'पत्र', iast: 'patra', gloss: 'leaf, letter, document', band: 'conjunct' },
  { id: 'gotra', deva: 'गोत्र', iast: 'gotra', gloss: 'lineage, clan', band: 'conjunct' },
  { id: 'shri', deva: 'श्री', iast: 'śrī', gloss: 'splendor, prosperity, holy', band: 'conjunct' },
  { id: 'shravana', deva: 'श्रवण', iast: 'śravaṇa', gloss: 'hearing, the ear', band: 'conjunct' },
  { id: 'ashrama', deva: 'आश्रम', iast: 'āśrama', gloss: 'hermitage, stage of life', band: 'conjunct' },
  { id: 'mishra', deva: 'मिश्र', iast: 'miśra', gloss: 'mixed', band: 'conjunct' },
  { id: 'shraddha', deva: 'श्रद्धा', iast: 'śraddhā', gloss: 'faith, reverence', band: 'conjunct' },
  { id: 'brahma', deva: 'ब्रह्म', iast: 'brahma', gloss: 'Brahman, the Absolute', band: 'conjunct' },
  { id: 'brahmana', deva: 'ब्राह्मण', iast: 'brāhmaṇa', gloss: 'priest; class of texts', band: 'conjunct' },
  { id: 'simha', deva: 'सिंह', iast: 'siṃha', gloss: 'lion (anusvāra)', band: 'conjunct' },
  { id: 'samsara', deva: 'संसार', iast: 'saṃsāra', gloss: 'cycle of existence, the world', band: 'conjunct' },
  { id: 'samgita', deva: 'संगीत', iast: 'saṃgīta', gloss: 'music', band: 'conjunct' },
  { id: 'samskrta', deva: 'संस्कृत', iast: 'saṃskṛta', gloss: 'Sanskrit; refined', band: 'conjunct' },
  { id: 'mangala', deva: 'मंगल', iast: 'maṃgala', gloss: 'auspiciousness, welfare', band: 'conjunct' },
  { id: 'bindu', deva: 'बिंदु', iast: 'bindu', gloss: 'point, drop', band: 'conjunct' },
  { id: 'ramah', deva: 'रामः', iast: 'rāmaḥ', gloss: 'Rāma (nominative, visarga)', band: 'conjunct' },
  { id: 'devah', deva: 'देवः', iast: 'devaḥ', gloss: 'god (nominative, visarga)', band: 'conjunct' },
  { id: 'namah', deva: 'नमः', iast: 'namaḥ', gloss: 'salutation, homage (visarga)', band: 'conjunct' },
  { id: 'duhkha', deva: 'दुःख', iast: 'duḥkha', gloss: 'suffering, sorrow (visarga)', band: 'conjunct' },
  { id: 'manah', deva: 'मनः', iast: 'manaḥ', gloss: 'mind (manas, visarga)', band: 'conjunct' },
  { id: 'tejah', deva: 'तेजः', iast: 'tejaḥ', gloss: 'splendor, energy (tejas, visarga)', band: 'conjunct' },
  { id: 'agnih', deva: 'अग्निः', iast: 'agniḥ', gloss: 'fire, Agni (nominative)', band: 'conjunct' },
  { id: 'putra', deva: 'पुत्र', iast: 'putra', gloss: 'son', band: 'conjunct' },
  { id: 'candra', deva: 'चन्द्र', iast: 'candra', gloss: 'moon', band: 'conjunct' },
  { id: 'sambhava', deva: 'सम्भव', iast: 'sambhava', gloss: 'origin, birth, possibility', band: 'conjunct' },
  { id: 'sampada', deva: 'सम्पद', iast: 'sampada', gloss: 'wealth, success, attainment', band: 'conjunct' },
  { id: 'tarka', deva: 'तर्क', iast: 'tarka', gloss: 'reasoning, logic', band: 'conjunct' },
  { id: 'parva', deva: 'पर्व', iast: 'parva', gloss: 'joint, festival, section', band: 'conjunct' },
];

// Grammar pools. Here `gloss` is the grammatical LABEL (the answer for
// `pick-gloss`, the prompt for `pick-form`) and `note` carries the role/meaning
// shown as feedback. `band` is repurposed as a CATEGORY bucket so multiple-choice
// distractors stay within a category (cases vs verbs) in the combined pool —
// these pools are never used for script (simple/cv/conjunct) drills.

// देव (deva) — a-stem masculine, singular. Identifying and producing these is
// the heart of the case (vibhakti) lessons.
const GRAMMAR_CASES: WordItem[] = [
  { id: 'nom', deva: 'देवः', iast: 'devaḥ', gloss: 'nominative', note: 'the subject — devaḥ gacchati, “the god goes”', band: 'conjunct' },
  { id: 'acc', deva: 'देवम्', iast: 'devam', gloss: 'accusative', note: 'the object — devam paśyati, “(he) sees the god”', band: 'conjunct' },
  { id: 'ins', deva: 'देवेन', iast: 'devena', gloss: 'instrumental', note: 'by / with — “by the god”', band: 'conjunct' },
  { id: 'dat', deva: 'देवाय', iast: 'devāya', gloss: 'dative', note: 'to / for — “for the god”', band: 'conjunct' },
  { id: 'abl', deva: 'देवात्', iast: 'devāt', gloss: 'ablative', note: 'from — “from the god”', band: 'conjunct' },
  { id: 'gen', deva: 'देवस्य', iast: 'devasya', gloss: 'genitive', note: 'of / ’s — “of the god”', band: 'conjunct' },
  { id: 'loc', deva: 'देवे', iast: 'deve', gloss: 'locative', note: 'in / on — “in the god”', band: 'conjunct' },
  { id: 'voc', deva: 'देव', iast: 'deva', gloss: 'vocative', note: 'address — “O god!”', band: 'conjunct' },
];

// √bhū "to be, become" — present active. Person + number in the ending.
const GRAMMAR_VERBS: WordItem[] = [
  { id: 'v3s', deva: 'भवति', iast: 'bhavati', gloss: '3rd person sg.', note: 'he / she / it becomes', band: 'cv' },
  { id: 'v3d', deva: 'भवतः', iast: 'bhavataḥ', gloss: '3rd person dual', note: 'the two of them become', band: 'cv' },
  { id: 'v3p', deva: 'भवन्ति', iast: 'bhavanti', gloss: '3rd person pl.', note: 'they become', band: 'cv' },
  { id: 'v2s', deva: 'भवसि', iast: 'bhavasi', gloss: '2nd person sg.', note: 'you become', band: 'cv' },
  { id: 'v2p', deva: 'भवथ', iast: 'bhavatha', gloss: '2nd person pl.', note: 'you all become', band: 'cv' },
  { id: 'v1s', deva: 'भवामि', iast: 'bhavāmi', gloss: '1st person sg.', note: 'I become', band: 'cv' },
  { id: 'v1p', deva: 'भवामः', iast: 'bhavāmaḥ', gloss: '1st person pl.', note: 'we become', band: 'cv' },
];

export const WORD_POOLS: Record<string, WordItem[]> = {
  devanagari: DEVANAGARI,
  'grammar-cases': GRAMMAR_CASES,
  'grammar-verbs': GRAMMAR_VERBS,
  // Combined pool for the final mixed check (noun forms + verb forms together).
  'grammar-forms': [...GRAMMAR_CASES, ...GRAMMAR_VERBS],
};

/** All items for a pool key, optionally filtered to a band ('mixed' = all). */
export function poolItems(poolKey: string, band: 'simple' | 'cv' | 'conjunct' | 'mixed'): WordItem[] {
  const all = WORD_POOLS[poolKey] ?? [];
  if (band === 'mixed') return all;
  return all.filter((w) => w.band === band);
}
